// components/auth/steps/OtpStep.jsx
import React, { useState, useEffect, useRef } from "react";
import { Button, Input } from "antd";
import { FiPhone, FiArrowLeft, FiShield } from "react-icons/fi";
import { useFormik } from "formik";
import { otpSchema } from "../../../validations/authValidation";

const OtpStep = ({
  mobile,
  onSubmit,
  onResend,
  onBack,
  loading,
  isDarkMode,
}) => {
  const [timer, setTimer] = useState(59);
  const [canResend, setCanResend] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      setOtpError(null);
      const result = await onSubmit(mobile, values.otp);
      if (result?.error) setOtpError(result.error);
    },
  });

  useEffect(() => {
    if (timer === 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = async () => {
    if (!canResend) return;
    try {
      await onResend();
      setTimer(59);
      setCanResend(false);
      setOtpError(null);
    } catch (error) {
      setOtpError("Failed to resend OTP. Please try again.");
    }
  };

  const handleOtpChange = (value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    formik.setFieldValue("otp", numericValue);
    if (otpError) setOtpError(null);
  };

  const handleInputChange = (index, value) => {
    const newOtp = (formik.values.otp || "").split("");
    newOtp[index] = value.replace(/\D/g, "");
    handleOtpChange(newOtp.join(""));

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !formik.values.otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getError = (field) => formik.touched[field] && formik.errors[field];

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            OTP sent to
          </p>
          <p
            className={`text-sm font-medium flex items-center gap-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}
          >
            <FiPhone size={14} className="text-green-600" />
            +91 {mobile}
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-medium text-green-600 hover:text-green-700"
        >
          Change
        </button>
      </div>

      <div>
        <label
          className={`text-xs font-medium mb-2 block ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          Enter 6-digit OTP
        </label>
        <div className="flex justify-between gap-2">
          {[...Array(6)].map((_, index) => (
            <Input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              value={formik.values.otp[index] || ""}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              id={`otp-${index}`}
              maxLength={1}
              className={`w-10 h-10 text-center text-lg font-bold ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            />
          ))}
        </div>
        {otpError && (
          <div className="mt-1 text-xs text-red-500">{otpError}</div>
        )}
        {getError("otp") && !otpError && (
          <div className="mt-1 text-xs text-red-500">{getError("otp")}</div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleResend}
          disabled={!canResend}
          className={`text-xs font-medium ${
            canResend
              ? "text-green-600 hover:text-green-700"
              : isDarkMode
                ? "text-gray-500 cursor-not-allowed"
                : "text-gray-400 cursor-not-allowed"
          }`}
        >
          Resend OTP
        </button>
        {!canResend && (
          <span
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            in 00:{timer.toString().padStart(2, "0")}s
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
      >
        <FiArrowLeft size={12} /> Back to mobile number
      </button>

      <Button
        type="primary"
        size="large"
        block
        htmlType="submit"
        loading={loading}
        className="mt-2 bg-green-600 hover:bg-green-700"
      >
        Verify & Login
      </Button>

      <div
        className={`p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? "bg-gray-800" : "bg-green-50"}`}
      >
        <FiShield
          className={`mt-0.5 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
          size={16}
        />
        <p
          className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          Your data is end-to-end encrypted and 100% safe
        </p>
      </div>
    </form>
  );
};

export default OtpStep;
